import { ReactComponent as RewardWhiteIcon } from '../../../../assets/images/icons/reward-white.svg';
import { ReactComponent as RewardBlueIcon } from '../../../../assets/images/icons/reward-blue.svg';
import { ReactComponent as MarketingManagerIcon } from '../../../../assets/images/icons/marketing-manager.svg';
import { ReactComponent as PublicistIcon } from '../../../../assets/images/icons/publicist.svg';
import { ReactComponent as ArtistIcon } from '../../../../assets/images/icons/artist.svg';
import { ReactComponent as AudioIcon } from '../../../../assets/images/icons/audio.svg';
import { ReactComponent as CreatorIcon } from '../../../../assets/images/icons/creator.svg';
import { ReactComponent as AnalystIcon } from '../../../../assets/images/icons/analyst.svg';
import { ReactComponent as ProfessorUIcon } from '../../../../assets/images/icons/professor.svg';
import { ReactComponent as AmplitudeIcon } from '../../../../assets/images/icons/button-icon.svg';
import { useState } from 'react';
import { Lock } from 'lucide-react';
import RewardModal from './RewardModal';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MusicLoader from '../../../../helper/MusicLoader';
import { useRewardNotification } from '../../../../queries/professoru/forum.query';
import { useMetaCampaignList } from '../../../../queries/growthEngine/metaCampaign.query';
import RewardProgressBar from './RewardProgressBar';

/* ---------------- HELPERS ---------------- */
const getRewardUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

const isExternalUrl = (url) => /^https?:\/\//i.test(url);

/* ---------------- PROFILE-VIEW REWARDS ---------------- */
const PROFILE_VIEW_REWARDS = {
  "Spotlight": "Artist Spotlight",
  "On The Playlist": "To add in UpNComer's Spotify playlist",
  "On The Record": "Featured interview on UpNComer + ProfessorU",
  "In The Feed": "Latest release share on UpNComer's Instagram",
};

/* ---------------- LOCKED MODULES ---------------- */
const LOCKED_MODULE_TITLES = ["Content Creator", "Publicist"];

/* ---------------- MODULE ICON MAP ---------------- */
const moduleIconMap = {
  "Growth Engine": MarketingManagerIcon,
  "Publicist": PublicistIcon,
  "Artist Manager": ArtistIcon,
  "UrStudio": AudioIcon,
  "Content Creator": CreatorIcon,
  "Data Analyst": AnalystIcon,
  "ProfessorU": ProfessorUIcon,
  "Amplitude AI": AmplitudeIcon,
};

/* ---------------- REWARD CARD ---------------- */
const RewardCard = ({ data, label, Icon, highlight }) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { mutate: sendRewardNotification, isPending: isNotifying } = useRewardNotification();
  return (
    <div
      onClick={() => setOpen(!open)}
      className={`
  w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16
  rounded-lg flex flex-col items-center justify-start pt-1
  ${highlight ? "bg-theme_clr2 text-white" : "bg-gray-100 text-theme_clr1"}
  cursor-pointer shrink-0
`}
    >
      {/* Label inside box */}
      <span
        className={`text-[10px] sm:text-[11px] md:text-[12px] font-semibold leading-none ${highlight ? "text-white" : "text-theme_clr1"
          }`}
      >
        {label}
      </span>

      {/* Icon */}
      <div className="flex-1 flex items-center justify-center">
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />
      </div>
      <RewardModal
        data={data}
        isOpen={open}
        onClose={() => setOpen(!open)}
        onCTA={() => {
          const profileViewText = PROFILE_VIEW_REWARDS[data.title];
          if (profileViewText) {
            sendRewardNotification({ id: data.id, text: profileViewText, title: data.title });
            return;
          }
          if (!data.url) return;
          if (isExternalUrl(data.url)) {
            sendRewardNotification({ id: data.id, text: "Book a 1:1 session with UpNComer", title: data.title });
            window.open(data.url, '_blank', 'noopener,noreferrer');
          } else {
            navigate(data.url);
          }
        }}
      />
    </div>
  );
};

/* ---------------- MODULE ROW ---------------- */
const ModuleRow = ({ title, Icon, completedRewards = 0, totalRewards = 8, rewards = [], isModuleLocked = false }) => {
  return (
    <div className="bg-theme_clr1 rounded-xl px-3 sm:px-4 py-3 shadow-sm space-y-1 sm:space-y-2">

      {/* MOBILE: label + progress on same line */}
      <div className="flex sm:hidden items-center justify-between gap-2 text-white font-medium">
        <div className="flex items-center gap-2 group relative">
          <Icon className="w-4 h-4" />
          <span className="text-sm">{title}</span>
          {isModuleLocked && <Lock className="w-3.5 h-3.5" aria-hidden="true" />}
          {isModuleLocked && (
            <span
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-max max-w-[calc(100vw-2rem)] rounded bg-gray-900 px-3 py-2 text-center text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
            >
              Work In Progress
            </span>
          )}
        </div>
        <div className="flex-1 max-w-[120px]">
          <RewardProgressBar completed={completedRewards} total={totalRewards} size="sm" />
        </div>
      </div>

      {/* DESKTOP: label + progress on same line */}
      <div className="hidden sm:flex items-center justify-between gap-3">

        {/* Left Label */}
        <div className="flex items-center justify-between gap-3 text-white font-medium min-w-[140px] md:min-w-[200px] flex-1">
          <div className="flex items-center gap-2 group relative">
            <Icon className="w-5 h-5" />
            <span>{title}</span>
            {isModuleLocked && <Lock className="w-4 h-4" aria-hidden="true" />}
            {isModuleLocked && (
              <span
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-max max-w-[calc(100vw-2rem)] rounded bg-gray-900 px-3 py-2 text-center text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
              >
                Work In Progress
              </span>
            )}
          </div>
          <div className="w-[120px] md:w-[140px]">
            <RewardProgressBar completed={completedRewards} total={totalRewards} size="sm" />
          </div>
        </div>

        {/* Rewards */}
        <div className="flex gap-3 overflow-x-auto no-scrollbar">
          {rewards.map((reward, index) => {
            const label = `Reward ${reward.order}`;
            const isHighlighted = !isModuleLocked && reward.isCompleted;
            const rewardData = {
              label,
              id: reward.rewardId,
              title: reward.title,
              description: reward.description,
              unlock: reward.unlockCondition?.description,
              unlockReward: reward.unlockCondition?.unlocks,
              unlockValue: reward.unlockCondition?.value,
              isUnlocked: isModuleLocked ? false : reward.isUnlocked,
              isActive: reward.isActive,
              isCompleted: isModuleLocked ? false : reward.isCompleted,
              isClaimed: isModuleLocked ? false : reward.isClaimed,
              isLocked: isModuleLocked ? true : reward.isLocked,
              ctaText: reward.ctaText || (reward.isUnlocked ? "Go Now" : "Unlock Now"),
              url: getRewardUrl(reward.path)
            };
            return (
              <RewardCard
                key={reward.rewardId || index}
                data={rewardData}
                label={label}
                Icon={isHighlighted ? RewardWhiteIcon : RewardBlueIcon}
                highlight={isHighlighted}
              />
            );
          })}
        </div>
      </div>

      {/* MOBILE: rewards row */}
      <div className="flex sm:hidden gap-2 overflow-x-auto no-scrollbar">
        {rewards.map((reward, index) => {
          const label = `Reward ${reward.order}`;
          const isHighlighted = !isModuleLocked && (reward.isUnlocked || reward.isCompleted);
          const rewardData = {
            label,
            id: reward.rewardId,
            title: reward.title,
            description: reward.description,
            unlock: reward.unlockCondition?.description,
            unlockReward: reward.unlockCondition?.unlocks,
            unlockValue: reward.unlockCondition?.value,
            isUnlocked: isModuleLocked ? false : reward.isUnlocked,
            isActive: reward.isActive,
            isCompleted: isModuleLocked ? false : reward.isCompleted,
            isClaimed: isModuleLocked ? false : reward.isClaimed,
            isLocked: isModuleLocked ? true : reward.isLocked,
            ctaText: reward.ctaText || (reward.isUnlocked ? "Go Now" : "Unlock Now"),
            url: getRewardUrl(reward.path)
          };
          return (
            <RewardCard
              key={reward.rewardId || index}
              data={rewardData}
              label={label}
              Icon={isHighlighted ? RewardWhiteIcon : RewardBlueIcon}
              highlight={isHighlighted}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- MAIN ---------------- */
const RewardList = ({ rewardsData = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-start justify-center">
        <MusicLoader />
      </div>
    );
  }

  if (!rewardsData.length) {
    return (
      <div className="min-h-screen bg-white flex items-start justify-center text-theme_clr1 text-sm">
        No rewards available
      </div>
    );
  }

  const sortedRewardsData = [...rewardsData].sort((a, b) => {
    const aLocked = LOCKED_MODULE_TITLES.includes(a.moduleTitle);
    const bLocked = LOCKED_MODULE_TITLES.includes(b.moduleTitle);
    if (aLocked === bLocked) return 0;
    return aLocked ? 1 : -1;
  });

  return (
    <div className="min-h-screen bg-white p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3">
      {sortedRewardsData.map((module, index) => (
        <ModuleRow
          key={module.moduleId || index}
          title={module.moduleTitle}
          Icon={moduleIconMap[module.moduleTitle] || ProfessorUIcon}
          completedRewards={module.completedRewards || 0}
          totalRewards={module.totalRewards || 8}
          rewards={module.rewards || []}
          isModuleLocked={LOCKED_MODULE_TITLES.includes(module.moduleTitle)}
        />
      ))}
    </div>
  );
};

export default RewardList;